import React from 'react';
import {
  Text,
  Input,
  Label,
  Dropdown,
  Option,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import DatePicker from '../DatePicker';
import {
  ArrowRight24Regular,
  Receipt24Regular,
} from '@fluentui/react-icons';
import { SectionShell } from './SectionShell';
import { BillingAddressCard } from './BillingAddressCard';
import {
  PAYMENT_TERMS_OPTIONS,
  DEFAULT_PAYMENT_TERMS,
} from '../DocumentPreview';

const paymentTermsLabel = (key?: string) =>
  PAYMENT_TERMS_OPTIONS.find((o) => o.key === key)?.label ??
  PAYMENT_TERMS_OPTIONS.find((o) => o.key === DEFAULT_PAYMENT_TERMS)!.label;

// Convert between YYYY-MM-DD strings (the form's wire format, ultimately
// pinned to midnight UTC at submit time) and JS Date objects, using LOCAL
// time so the calendar UI doesn't drift a day for users east of UTC.
const isoToDate = (iso: string): Date | null => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const dateToIso = (date: Date | null | undefined): string => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const formatDateShort = (date?: Date): string => {
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Monthly rentals: the customer picks a calendar month and the rental window
// spans it end-to-end (1st → last day). monthBoundsIso returns those two edges
// as YYYY-MM-DD strings so the parent stores them in the same pickup/return
// fields a dated order uses — pricing and Booqable stay period-driven.
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const monthBoundsIso = (year: number, monthIdx: number): { first: string; last: string } => {
  const first = new Date(year, monthIdx, 1);
  const last = new Date(year, monthIdx + 1, 0); // day 0 of next month = last day
  return { first: dateToIso(first), last: dateToIso(last) };
};

const useStyles = makeStyles({
  pickupSection: {
    display: 'flex',
    gap: '12px',
    // Align children to their bottom edges so the arrow wrap (40px tall,
    // matching the large Input) sits flush with the inputs below the
    // column headers — keeps the arrow vertically centered with them.
    alignItems: 'flex-end',
    // Two date columns + an arrow between are too cramped at 375px.
    // Stack the columns and hide the connecting arrow on mobile.
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '12px',
    },
  },
  pickupColumn: {
    flex: 1,
  },
  pickupColumnHeader: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  dateInput: {
    justifyContent: 'flex-start',
    color: tokens.colorNeutralForeground3,
  },
  arrowWrap: {
    display: 'flex',
    alignItems: 'center',
    // Matches Fluent v9 Input size="large" height so the arrow centers
    // vertically with the input glyph row.
    height: '40px',
    flexShrink: 0,
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
});

interface BillingRentalSectionProps {
  savedBillingAddress: string | null;
  onAddBillingAddress: () => void;
  onChangeBillingAddress: () => void;
  /** Clears the saved billing address. Returns the section to the empty /
   *  "Add billing address" state. */
  onRemoveBillingAddress?: () => void;
  pickupDate: string;
  returnDate: string;
  onPickupDateChange: (value: string) => void;
  onReturnDateChange: (value: string) => void;
  /** Monthly-rental mode. When true, the two date pickers are replaced by a
   *  single month picker whose selection is written back to pickupDate /
   *  returnDate as the chosen calendar month's first and last day. */
  monthly?: boolean;
  /** Toggling this renders the "Monthly rental" switch. When absent, the switch
   *  is hidden (e.g. project-phase umbrellas, which own their own date model). */
  onMonthlyChange?: (value: boolean) => void;
  /** Optional PO Number — surfaces on the AN merchant receipt and invoice
   *  documents. Mainly used for government / commercial customers. */
  poNumber?: string;
  onPoNumberChange?: (value: string) => void;
  /** When the order's customer requires a PO, the field reads "(required)"
   *  instead of "(optional)". The hard block stays at invoice finalize. */
  poRequired?: boolean;
  /** Invoice payment-terms key (e.g. 'net_30'). Seeds the terms dropdown;
   *  defaults to 'due_on_receipt' when absent. */
  paymentTerms?: string;
  /** When provided, the Payment terms row renders as a dropdown and reports the
   *  chosen key here (the parent persists it onto the order). Absent → the
   *  terms render read-only. */
  onPaymentTermsChange?: (termKey: string) => void;
  /** Optional real-life project name (orders.project_name) — a free-text label
   *  (e.g. a contractor's job name) that renders on the invoice. Distinct from
   *  the multi-phase project model. Omit onProjectNameChange to render read-only. */
  projectName?: string;
  onProjectNameChange?: (value: string) => void;
  showIcon?: boolean;
  /** Project phase mode: dates belong to a contiguous swap chain. The internal
   *  "collapse return to pickup" guard is skipped (the parent owns the cascade),
   *  individual pickers can be locked, and an explanatory note + footer (save /
   *  shift-later toggle) render around the dates. */
  phaseMode?: boolean;
  /** Label the two pickers as the project's overall window — "Start" /
   *  "Completion" instead of "Delivery" / "Pick up". Used on a multi-phase
   *  order's umbrella row, where the dates span the phases below rather than a
   *  single physical delivery/pickup pair. */
  projectWindow?: boolean;
  pickupDisabled?: boolean;
  returnDisabled?: boolean;
  datesNote?: string;
  datesFooter?: React.ReactNode;
  /** Optional YYYY-MM-DD calendar bounds. When omitted the pickup picker falls
   *  back to its default min of the delivery date. Used in phase mode to enforce
   *  strict start < end against the edited phase and its neighbours. */
  deliveryMinDate?: string;
  deliveryMaxDate?: string;
  pickupMinDate?: string;
  pickupMaxDate?: string;
  /** Lock the PO field (project child orders inherit the project's PO). */
  poReadOnly?: boolean;
  poNote?: string;
  /** Lock the whole section to view-only (dispatched/completed order): billing
   *  address buttons, both date pickers, and the PO field are all disabled. */
  readOnly?: boolean;
}

const BillingRentalSectionBase: React.FC<BillingRentalSectionProps> = ({
  savedBillingAddress,
  onAddBillingAddress,
  onChangeBillingAddress,
  onRemoveBillingAddress,
  pickupDate,
  returnDate,
  onPickupDateChange,
  onReturnDateChange,
  monthly = false,
  onMonthlyChange,
  poNumber,
  onPoNumberChange,
  poRequired,
  paymentTerms,
  onPaymentTermsChange,
  projectName,
  onProjectNameChange,
  showIcon = true,
  phaseMode = false,
  projectWindow = false,
  pickupDisabled = false,
  returnDisabled = false,
  datesNote,
  datesFooter,
  deliveryMinDate,
  deliveryMaxDate,
  pickupMinDate,
  pickupMaxDate,
  poReadOnly = false,
  poNote,
  readOnly = false,
}) => {
  const styles = useStyles();
  // A locked order disables every editable affordance in this section.
  const datesLocked = pickupDisabled || readOnly;
  const returnLocked = returnDisabled || readOnly;
  const poLocked = poReadOnly || readOnly;
  // On a multi-phase umbrella the two dates describe the project's overall
  // span, not a single delivery/pickup — relabel accordingly.
  const startLabel = projectWindow ? 'Start' : 'Delivery';
  const endLabel = projectWindow ? 'Completion' : 'Pick up';

  // Monthly mode: the selected month is derived from the stored pickup date
  // (its first-of-month), falling back to the current month for a fresh order.
  // Changing either dropdown re-emits the whole month's bounds so pickup/return
  // stay a valid calendar-month window.
  const seedMonthDate = isoToDate(pickupDate) ?? new Date();
  const selMonthIdx = seedMonthDate.getMonth();
  const selYear = seedMonthDate.getFullYear();
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from(
    new Set([...Array.from({ length: 5 }, (_, i) => currentYear - 1 + i), selYear]),
  ).sort((a, b) => a - b);
  const emitMonth = (year: number, monthIdx: number) => {
    const { first, last } = monthBoundsIso(year, monthIdx);
    onPickupDateChange(first);
    onReturnDateChange(last);
  };
  return (
    <SectionShell title="Billing & rental period" icon={<Receipt24Regular />} showIcon={showIcon}>
      <div style={{ marginBottom: '16px' }}>
        <BillingAddressCard
          savedBillingAddress={savedBillingAddress}
          onAddBillingAddress={onAddBillingAddress}
          onChangeBillingAddress={onChangeBillingAddress}
          onRemoveBillingAddress={onRemoveBillingAddress}
          readOnly={readOnly}
        />
      </div>

      {onMonthlyChange && (
        <div style={{ marginBottom: '12px' }}>
          <Switch
            checked={monthly}
            disabled={readOnly}
            label="Monthly rental"
            onChange={(_, d) => {
              onMonthlyChange(d.checked);
              // Snap the window to the seed month's bounds the moment monthly
              // turns on, so pickup/return are a valid full-month range without
              // the user having to touch the dropdowns.
              if (d.checked) emitMonth(selYear, selMonthIdx);
            }}
          />
          <Text
            style={{
              display: 'block',
              marginTop: '2px',
              fontSize: '13px',
              color: tokens.colorNeutralForeground3,
            }}
          >
            {monthly
              ? 'Billed for the whole calendar month; pick the rental month below.'
              : 'Turn on to rent by the calendar month instead of specific dates.'}
          </Text>
        </div>
      )}
      {datesNote && (
        <Text
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '13px',
            color: tokens.colorNeutralForeground3,
          }}
        >
          {datesNote}
        </Text>
      )}
      {monthly ? (
        <div className={styles.pickupSection}>
          <div className={styles.pickupColumn}>
            <Text className={styles.pickupColumnHeader}>Rental month</Text>
            <Dropdown
              size="large"
              value={MONTH_LABELS[selMonthIdx]}
              selectedOptions={[String(selMonthIdx)]}
              disabled={datesLocked}
              onOptionSelect={(_, d) => {
                if (d.optionValue != null) emitMonth(selYear, Number(d.optionValue));
              }}
              style={{ width: '100%' }}
            >
              {MONTH_LABELS.map((m, i) => (
                <Option key={i} value={String(i)}>
                  {m}
                </Option>
              ))}
            </Dropdown>
          </div>
          <div className={styles.pickupColumn}>
            <Text className={styles.pickupColumnHeader}>Year</Text>
            <Dropdown
              size="large"
              value={String(selYear)}
              selectedOptions={[String(selYear)]}
              disabled={datesLocked}
              onOptionSelect={(_, d) => {
                if (d.optionValue != null) emitMonth(Number(d.optionValue), selMonthIdx);
              }}
              style={{ width: '100%' }}
            >
              {yearOptions.map((y) => (
                <Option key={y} value={String(y)}>
                  {String(y)}
                </Option>
              ))}
            </Dropdown>
          </div>
        </div>
      ) : (
        <div className={styles.pickupSection}>
          <div className={styles.pickupColumn}>
            <Text className={styles.pickupColumnHeader}>{startLabel}</Text>
            <DatePicker
              size="large"
              value={isoToDate(pickupDate)}
              disabled={datesLocked}
              onSelectDate={(date) => {
                const nextPickup = dateToIso(date);
                onPickupDateChange(nextPickup);
                // One-off orders collapse the return to the pickup when the new
                // pickup overshoots it (avoids an invalid range). In phase mode
                // the parent owns the contiguous-chain math, so skip this.
                if (!phaseMode && date && returnDate) {
                  const ret = isoToDate(returnDate);
                  if (ret && date.getTime() > ret.getTime()) {
                    onReturnDateChange(nextPickup);
                  }
                }
              }}
              formatDate={formatDateShort}
              placeholder={projectWindow ? 'Select start date' : 'Select delivery date'}
              minDate={deliveryMinDate ? isoToDate(deliveryMinDate) ?? undefined : undefined}
              maxDate={deliveryMaxDate ? isoToDate(deliveryMaxDate) ?? undefined : undefined}
              highlightedRange={{
                from: isoToDate(pickupDate),
                to: isoToDate(returnDate),
              }}
              style={{ width: '100%' }}
            />
          </div>
          <div className={styles.arrowWrap}>
            <ArrowRight24Regular style={{ color: tokens.colorNeutralForeground3 }} />
          </div>
          <div className={styles.pickupColumn}>
            <Text className={styles.pickupColumnHeader}>{endLabel}</Text>
            <DatePicker
              size="large"
              value={isoToDate(returnDate)}
              disabled={returnLocked}
              onSelectDate={(date) => onReturnDateChange(dateToIso(date))}
              formatDate={formatDateShort}
              placeholder={projectWindow ? 'Select completion date' : 'Select pick up date'}
              minDate={(pickupMinDate ? isoToDate(pickupMinDate) : isoToDate(pickupDate)) ?? undefined}
              maxDate={pickupMaxDate ? isoToDate(pickupMaxDate) ?? undefined : undefined}
              highlightedRange={{
                from: isoToDate(pickupDate),
                to: isoToDate(returnDate),
              }}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      )}
      {datesFooter && <div style={{ marginTop: '12px' }}>{datesFooter}</div>}
      {(onPoNumberChange || poLocked) && (
        <div style={{ marginTop: '16px' }}>
          <Label htmlFor="po-number" weight="semibold">PO Number {poRequired ? '(required)' : '(optional)'}</Label>
          <Input
            id="po-number"
            size="large"
            value={poNumber ?? ''}
            onChange={poLocked ? undefined : ((_, data) => onPoNumberChange?.(data.value))}
            readOnly={poLocked}
            disabled={poLocked}
            placeholder={poLocked ? '—' : undefined}
            maxLength={100}
            style={{ width: '100%' }}
          />
          {poNote && (
            <Text style={{ display: 'block', marginTop: '4px', fontSize: '13px', color: tokens.colorNeutralForeground3 }}>
              {poNote}
            </Text>
          )}
        </div>
      )}
      {(onPaymentTermsChange || readOnly) && (
        <div style={{ marginTop: '16px' }}>
          <Label htmlFor="payment-terms" weight="semibold">Payment terms</Label>
          <div>
            {onPaymentTermsChange && !readOnly ? (
              <Dropdown
                id="payment-terms"
                size="large"
                value={paymentTermsLabel(paymentTerms)}
                selectedOptions={[paymentTerms || DEFAULT_PAYMENT_TERMS]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) onPaymentTermsChange(d.optionValue);
                }}
                style={{ width: '100%' }}
              >
                {PAYMENT_TERMS_OPTIONS.map((o) => (
                  <Option key={o.key} value={o.key}>
                    {o.label}
                  </Option>
                ))}
              </Dropdown>
            ) : (
              <Input
                size="large"
                value={paymentTermsLabel(paymentTerms)}
                readOnly
                disabled
                style={{ width: '100%' }}
              />
            )}
          </div>
        </div>
      )}
      {(onProjectNameChange || (projectName ?? '') !== '') && (
        <div style={{ marginTop: '16px' }}>
          <Label htmlFor="project-name" weight="semibold">Project (optional)</Label>
          <Input
            id="project-name"
            size="large"
            value={projectName ?? ''}
            onChange={onProjectNameChange ? ((_, data) => onProjectNameChange(data.value)) : undefined}
            readOnly={!onProjectNameChange}
            disabled={!onProjectNameChange}
            placeholder={!onProjectNameChange ? '—' : 'e.g. Maple St. Remodel (shown on the invoice)'}
            maxLength={255}
            style={{ width: '100%' }}
          />
        </div>
      )}
    </SectionShell>
  );
};

export const BillingRentalSection = React.memo(BillingRentalSectionBase);
export default BillingRentalSection;
